/**
 * Quality Command Center data hooks — read the RLS-protected platform_qa_* tables
 * (internal-admin only). All reads fail closed: RLS returns 0 rows for non-admins,
 * and the UI degrades to UNKNOWN rather than PASS on missing/empty evidence.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { type QaResult, READINESS_LEVELS, evaluateLevel } from '@/lib/qa/commandCenterLogic';

export { READINESS_LEVELS, evaluateLevel };
export type { QaResult };

export const ENGINE_ORDER = ['chaos', 'soak', 'fuzz', 'security', 'performance', 'simulation', 'meta', 'migration'] as const;

export interface QaEngineResult {
  engine: string; result: QaResult;
  pass_count: number; fail_product: number; fail_harness: number;
  blocked_env: number; blocked_product: number; not_executed: number;
  seed: string | null; residue: number | null; buckets: Record<string, string[]>; captured_at: string;
}
export interface QaReadinessSnapshot {
  recommendation: string; status_counts: Record<string, number>;
  categories: Record<string, QaResult>; hard_blockers: string[];
  source_fingerprint: string | null; environment: string | null; captured_at: string;
}
export interface QaArtifactHealth {
  status?: 'HEALTHY' | 'INCOMPLETE' | 'ARTIFACT_ERROR';
  expected?: number; present?: string[]; missing?: string[]; malformed?: string[]; empty?: string[];
}
export interface QaSimEvent { t?: string; kind?: string; label?: string; tickets?: number; invariantViolations?: number; [k: string]: unknown; }
export interface QaBenchmark { label: string; min?: number; median?: number; p95?: number; max?: number; }
export interface QaSoakSeriesPoint { i: number; latencyMs: number; rssMb: number; }
export interface QaRun {
  run_id: string; engine: string; scenario: string | null; seed: string | null;
  started_at: string | null; finished_at: string | null; environment: string | null;
  project_ref?: string | null; fingerprint: string | null; result: QaResult;
  cleanup_status: string | null; residue: number | null; duration_ms: number | null;
  buckets?: Record<string, string[]>; metrics: Record<string, any>;
  replay?: Record<string, any>; cleanup_detail?: Record<string, any>;
  artifact_health?: QaArtifactHealth; sim_events?: QaSimEvent[]; schema_version?: number;
  // logical-run / attempt identity (Phase 1). run_id == attempt_id (unique per
  // attempt); logical_run_id groups attempts; is_authoritative marks the latest.
  logical_run_id?: string | null; attempt_id?: string | null;
  attempt_number?: number | null; is_authoritative?: boolean | null;
}
export interface QaSecurityMatrixRow {
  resource: string; action: string; role: string; relationship: string;
  ui: string | null; api: string | null; db: string | null; source: string;
}
export interface QaFinding {
  finding_id: string; engine: string | null; severity: string | null; classification: string | null;
  title: string | null; root_cause: string | null; status: string | null; source: string;
}
export interface QaCoverage {
  charter_id: string; persona: string; behavior_style: string; feature_area: string | null;
  last_run_at: string | null; run_count: number; last_outcome: string | null;
  last_verifier_outcome: string | null; open_finding_count: number; last_patch_sha: string | null; updated_at: string | null;
}
export interface QaWorkerHeartbeat {
  worker_id: string; last_heartbeat: string | null; worker_commit: string | null; worker_version: string | null;
  current_state: string | null; current_run_id: string | null; last_completed_run: string | null;
  pending_outbox_count: number | null; consecutive_infra_failures: number | null; hostname_hash: string | null; updated_at: string | null;
}
export interface QaAuditDebtItem { item: string; engine: string | null; category: string | null; reason: string | null; blocks_level: string | null; status: string; }
export interface QaMutation { mutation_id: string; category: string | null; killed: boolean; detail: string | null; run_id: string | null; }

function useQ<T>(key: unknown[], table: keyof Database['public']['Tables'], build: (q: any) => any) {
  return useQuery<T[]>({
    queryKey: key,
    queryFn: async () => {
      // `.from(table)` typed as a table-key union; `.select('*')` over the full
      // schema union hits TS2589, so read `from` through its minimal builder
      // surface (single structural annotation — not `any`/`as unknown as`).
      const fromTable: { select: (columns: string) => PromiseLike<{ data: T[] | null; error: unknown }> } =
        supabase.from(table);
      const { data, error } = await build(fromTable.select('*'));
      if (error) throw error;
      return (data ?? []) as T[];
    },
    staleTime: 30_000,
  });
}

export const useQaReadiness = () =>
  useQuery<QaReadinessSnapshot | null>({
    queryKey: ['qa-readiness'],
    queryFn: async () => {
      const { data, error } = await supabase.from('platform_qa_readiness_snapshots').select('*').order('captured_at', { ascending: false }).limit(1);
      if (error) throw error;
      return (data?.[0] as QaReadinessSnapshot) ?? null;
    },
    staleTime: 30_000,
  });

export const useQaEngines = () => useQ<QaEngineResult>(['qa-engines'], 'platform_qa_engine_results', (q) => q.order('engine'));
export const useQaRuns = () => useQ<QaRun>(['qa-runs'], 'platform_qa_runs', (q) => q.order('finished_at', { ascending: false, nullsFirst: false }).limit(200));
export const useQaFindings = () => useQ<QaFinding>(['qa-findings'], 'platform_qa_findings', (q) => q.order('severity'));
export const useQaAuditDebt = () => useQ<QaAuditDebtItem>(['qa-audit-debt'], 'platform_qa_audit_debt', (q) => q.order('item'));
export const useQaMutations = () => useQ<QaMutation>(['qa-mutations'], 'platform_qa_mutations', (q) => q.order('mutation_id'));
export const useQaCoverage = () => useQ<QaCoverage>(['qa-coverage'], 'platform_qa_coverage', (q) => q.order('feature_area').order('charter_id').order('persona').order('behavior_style'));
// Worker health/heartbeat (Phase 4 unattended worker). Sanitized: hostname is
// hashed, no secrets or private paths are ever published. Refetches on an
// interval so the live worker state is visible without a manual reload.
export const useQaWorkerHeartbeat = () =>
  useQuery<QaWorkerHeartbeat[]>({
    queryKey: ['qa-worker-heartbeat'],
    queryFn: async () => {
      const { data, error } = await supabase.from('platform_qa_worker_heartbeat').select('*').order('last_heartbeat', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as QaWorkerHeartbeat[];
    },
    staleTime: 15_000, refetchInterval: 30_000,
  });
export const useQaScenariosForRun = (runId: string | null) =>
  useQuery({ queryKey: ['qa-scenarios', runId], enabled: !!runId, queryFn: async () => { const { data, error } = await supabase.from('platform_qa_scenario_results').select('*').eq('run_id', runId).order('name'); if (error) throw error; return data ?? []; } });

/** A single run bundle (for the dedicated run-detail route). Returns null if not found. */
export const useQaRun = (runId: string | undefined) =>
  useQuery<QaRun | null>({
    queryKey: ['qa-run', runId], enabled: !!runId,
    queryFn: async () => {
      const { data, error } = await supabase.from('platform_qa_runs').select('*').eq('run_id', runId).limit(1);
      if (error) throw error;
      return (data?.[0] as QaRun) ?? null;
    },
  });

/** Findings attached to a specific run (source = `run:<runId>`). */
export const useQaFindingsForRun = (runId: string | undefined) =>
  useQuery<QaFinding[]>({
    queryKey: ['qa-findings-run', runId], enabled: !!runId,
    queryFn: async () => {
      const { data, error } = await supabase.from('platform_qa_findings').select('*').eq('source', `run:${runId}`);
      if (error) throw error;
      return (data ?? []) as QaFinding[];
    },
  });

export const useQaSecurityMatrix = () => useQ<QaSecurityMatrixRow>(['qa-security-matrix'], 'platform_qa_security_matrix', (q) => q.order('resource'));

/** All runs for a single engine (for Performance/Soak/Simulation views). */
export const useQaRunsByEngine = (engine: string) =>
  useQuery<QaRun[]>({
    queryKey: ['qa-runs-engine', engine],
    queryFn: async () => {
      const { data, error } = await supabase.from('platform_qa_runs').select('*').eq('engine', engine).order('finished_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as QaRun[];
    },
    staleTime: 30_000,
  });
